import { BoxProps } from '@mui/material';
import { differenceInMilliseconds, isSameDay, subHours } from 'date-fns';
import * as React from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { ONE_HOUR } from '../../../../config/constants';
import { useCurrentUserClient } from '../../../../contexts/OidcContext/OidcContext.selectors';
import { ganttPollingInterval } from '../../../pages/RmsPage';
import { useRmsGanttValue } from '../RmsContext';
import { ganttPxPerMs } from './gantt.constants';
import { GanttBox, GanttContentBox, StyledCurrentTimeIndicator, StyledPlayArrowIcon } from './Gantt.style';
import { getGanttNbTimelineHours } from './gantt.utils';
import { GanttGrid } from './GanttGrid';
import { GanttTimeline } from './GanttTimeline';

export const ganttGridPaddingLeft = 60;
export const Gantt = memo(function Gantt({
    children,
    shallGanttAutoScroll,
    disableGanttAutoScroll,
    ...layoutProps
}: {
    children?: React.ReactNode;
    shallGanttAutoScroll: boolean;
    disableGanttAutoScroll: () => void;
} & Partial<BoxProps>) {
    const resourceType = useRmsGanttValue('resourceType');
    const client = useCurrentUserClient();
    const selectedDay = useRmsGanttValue('day');
    const currentDateMinusDayHourShift = subHours(new Date(), client.startDayHourShift || 0);
    const ganttRef = useRef<HTMLDivElement>(null);
    const currentTimeIndicatorRef = useRef<HTMLDivElement>(null);
    const [ganttScrollYOffset, setGanttScrollYOffset] = useState(0);
    const [now, setNow] = useState(Date.now());
    const dateNowRef = useRef<NodeJS.Timer>();
    const dayBounds = useRmsGanttValue('dayBounds');
    const ganttNbTimelineHours = getGanttNbTimelineHours(dayBounds.from, dayBounds.to);

    // Calculate y offset on scroll to position vertical current time line
    const ganttContainerElement = ganttRef.current;
    useEffect(() => {
        const onScroll = () => setGanttScrollYOffset(ganttRef.current.scrollTop);
        if (ganttContainerElement) {
            ganttContainerElement.addEventListener('scroll', onScroll, { passive: true });
            return () => ganttContainerElement.removeEventListener('scroll', onScroll);
        }
    }, [ganttContainerElement]);

    // Update current time ganttPollingInterval secondes and convert time to px for use as current time indicator's left offset in px
    useEffect(() => {
        dateNowRef.current = setInterval(() => setNow(Date.now()), ganttPollingInterval);
        return () => {
            clearInterval(dateNowRef.current);
        };
    }, [dateNowRef]);
    const currentTimeInPx = useMemo(() => {
        return (
            (differenceInMilliseconds(now, dayBounds.from) - 30000) * ganttPxPerMs * 2 +
            ganttGridPaddingLeft
        ).toFixed(0);
    }, [dayBounds.from, now]);

    // if shallAutoScroll, scrollIntoView on current time indicator element whenever currentTimeInPx is updated
    const currentTimeIndicatorElement = currentTimeIndicatorRef.current;
    useEffect(() => {
        if (currentTimeIndicatorElement && shallGanttAutoScroll) {
            currentTimeIndicatorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [currentTimeIndicatorElement, shallGanttAutoScroll]);

    // Disable auto scroll when user scrolls manually (listening to wheel event)
    useEffect(() => {
        window.addEventListener('scroll', disableGanttAutoScroll, { capture: true });
        return () => {
            window.removeEventListener('scroll', disableGanttAutoScroll, { capture: true });
        };
    }, [disableGanttAutoScroll]);

    useEffect(() => {
        ganttRef.current.scrollTop = 0;
    }, [ganttRef, resourceType]);

    // ref={timelineRef}
    return (
        <GanttBox ref={ganttRef} {...layoutProps} paddingLeft={`${ganttGridPaddingLeft}px`}>
            {isSameDay(selectedDay, currentDateMinusDayHourShift) && (
                <StyledCurrentTimeIndicator
                    left={`${currentTimeInPx}px`}
                    top={`${ganttScrollYOffset - 15}px`}
                    ref={currentTimeIndicatorRef}
                >
                    <StyledPlayArrowIcon />
                </StyledCurrentTimeIndicator>
            )}

            <GanttTimeline />
            <GanttContentBox
                sx={{
                    width: ganttNbTimelineHours * 2 * ONE_HOUR * ganttPxPerMs,
                }}
            >
                <GanttGrid />
                {children}
            </GanttContentBox>
        </GanttBox>
    );
});
